import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  CheckCircleIcon,
  FileSignatureIcon,
  CreditCardIcon,
  WrenchIcon,
  BarChart3Icon,
} from "lucide-react";

// Simple Tailwind button so you don’t need a separate UI lib
const PrimaryButton = ({ children, className = "", ...props }) => (
  <button
    {...props}
    className={`bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-3 px-6 rounded-2xl shadow-md transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-600 ${className}`}
  >
    {children}
  </button>
);

const features = [
  {
    title: "Instant Lease Creation & e‑Sign",
    description:
      "Generate state‑compliant leases and get them signed online in minutes—no paper, no postage.",
    icon: <FileSignatureIcon className="w-6 h-6 text-emerald-600" />,
  },
  {
    title: "Automated Rent Collection",
    description:
      "Flat $4 covers secure online payments, automatic reminders, and next‑day payouts.",
    icon: <CreditCardIcon className="w-6 h-6 text-emerald-600" />,
  },
  {
    title: "Maintenance Made Simple",
    description:
      "Tenants create tickets, you pick from recommended contractors—ZenRent tracks it end‑to‑end.",
    icon: <WrenchIcon className="w-6 h-6 text-emerald-600" />,
  },
  {
    title: "Clear Income & Expense Reports",
    description:
      "Real‑time dashboards and tax‑ready exports keep your books—and your stress—clean.",
    icon: <BarChart3Icon className="w-6 h-6 text-emerald-600" />,
  },
];

export default function LandingPage() {
  return (
    <div className="font-sans text-gray-800 bg-emerald-50 min-h-screen flex flex-col">
      {/* Navigation */}
      <nav className="relative z-10 bg-white/80 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="text-2xl font-semibold text-gray-900">
            Zen<span className="text-emerald-600">Rent</span>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="/login"
              className="text-gray-700 hover:text-emerald-600 font-medium transition"
            >
              Sign In
            </a>
            <a
              href="/signup"
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2 px-5 rounded-lg shadow-sm transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-600"
            >
              Sign Up
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden bg-white">
        <img
          src="/images/minimal-room.png"
          alt="Minimal living room"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="relative max-w-6xl mx-auto px-6 py-32 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl sm:text-5xl md:text-6xl font-semibold leading-tight tracking-tight text-gray-900"
          >
            Rental management, <span className="text-emerald-600">simplified</span> <br />
            for small landlords
          </motion.h1>
          <p className="mt-6 text-lg sm:text-xl mx-auto max-w-2xl">
            Turn rentals back into passive investments.
            <br />
            Handle leases, payments, and AI managed maintenance.
          </p>
          <div className="mt-10 flex justify-center">
            <PrimaryButton onClick={() => (window.location.href = "/signup")} className="max-w-xs">Start your 30‑day free trial</PrimaryButton>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-semibold text-center mb-16">
            Everything you need—nothing you don’t
          </h2>
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((feat) => (
              <motion.div
                key={feat.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className="bg-emerald-100/40 rounded-2xl p-6 shadow-sm hover:shadow-md"
              >
                <div className="w-10 h-10 flex items-center justify-center mb-4">{feat.icon}</div>
                <h3 className="text-xl font-medium mb-2">{feat.title}</h3>
                <p className="text-sm leading-relaxed text-gray-700">{feat.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="mt-auto bg-emerald-600 text-white py-8">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-center gap-4">
          <p className="text-sm">© {new Date().getFullYear()} ZenRent. All rights reserved.</p>
          <span className="text-emerald-300">·</span>
          <Link to="/privacy" className="text-sm text-emerald-100 hover:text-white transition">
            Privacy Policy
          </Link>
          <span className="text-emerald-300">·</span>
          <Link to="/terms" className="text-sm text-emerald-100 hover:text-white transition">
            Terms &amp; Conditions
          </Link>
        </div>
      </footer>
    </div>
  );
}
